import { useMemo, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Label, Select, TextInput, Textarea } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useMutationData } from "../../../hooks/useMutationData";
import { useGetData } from "../../../hooks/useGetData";
import { EmpresaInterface } from "../../../interfaces/EmpresaInterface";
import { Loading } from "../../../components/Loading/Loading";
import { ThemeContext } from "../../../components/ThemeContext/ThemeContext";

export const EvaluadosCrearScreen = () => {
  const { control, handleSubmit } = useForm();
  const mutateEvaluado = useMutationData({ url: "/evaluado" });
  const navigate = useNavigate();
  const location = useLocation();

  const queryEmpresas = useGetData<EmpresaInterface[]>({
    url: `/empresa?estatus[eq]=1`,
    name: ["empresa"],
  });

  const { isDarkMode } = useContext(ThemeContext);

  const [numeroHijos, setNumeroHijos] = useState(0);

  const [tipoEvaluado, setTipoEvaluado] = useState<string>("todos");

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tipoParam = searchParams.get("tipo");

    if (tipoParam) {
      setTipoEvaluado(tipoParam);
      localStorage.setItem("ultimoTipoEvaluado", tipoParam);
    } else {
      const tipoGuardado = localStorage.getItem("ultimoTipoEvaluado");
      if (tipoGuardado) {
        setTipoEvaluado(tipoGuardado);
      }
    }
  }, [location.search]);

  const formatTitleCase = (value: string): string => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  // Función para formatear a minúsculas
  const formatLowerCase = (value: string): string => {
    if (!value) return value;
    return value.toLowerCase();
  };

  // Función para formatear a mayúsculas
  const formatUpperCase = (value: string): string => {
    if (!value) return value;
    return value.toUpperCase();
  };

  const handleNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: { onChange: (value: number) => void }
  ): void => {
    const value = Math.max(0, parseInt(e.target.value) || 0);
    field.onChange(value);
  };

  const handleCancel = () => {
    navigate(`/evaluadosList?tipo=${tipoEvaluado}`);
  };

  if (queryEmpresas.isLoading || mutateEvaluado.isPending) {
    return <Loading />;
  }

  return (
    <div
      className={`min-h-screen p-6 ${
        isDarkMode ? "bg-gray-800 text-white" : "bg-white text-black"
      }`}
    >
      <div
        className={`max-w-4xl mx-auto p-6 rounded-lg shadow-lg space-y-6 ${
          isDarkMode ? "bg-gray-700" : "bg-white"
        }`}
      >
        <div
          className={`bg-gradient-to-r from-green-500 to-blue-500 p-6 text-white rounded-t-lg`}
        >
          <h2 className="text-3xl font-bold text-center">
            Crear un nuevo Evaluado
          </h2>
        </div>
        <form
          onSubmit={handleSubmit((data) => {
            mutateEvaluado.mutate(
              {
                data: data,
                tipo: "post",
              },
              {
                onSuccess: () =>
                  navigate(`/evaluadosList?tipo=${tipoEvaluado}`),
              }
            );
          })}
          className="space-y-6"
        >
          {/* Fecha de Evaluación */}
          <div className="w-full md:w-[265px]">
            <Label htmlFor="fecha_evaluacione" value="Fecha de Evaluación" />
            <Controller
              name="fecha_evaluacione"
              control={control}
              defaultValue={null}
              render={({ field, fieldState }) => (
                <TextInput
                  id="fecha_evaluacione"
                  type="date"
                  {...field}
                  color={fieldState.error ? "failure" : "gray"}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Nombre */}
            <div className="space-y-2">
              <Label
                htmlFor="nombre"
                value="Nombre(s)"
                className="dark:text-white"
              />
              <Controller
                name="nombre"
                control={control}
                defaultValue=""
                rules={{
                  required: "Se requiere un nombre",
                  maxLength: {
                    value: 32,
                    message: "El nombre debe tener menos de 32 caracteres",
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="nombre"
                    placeholder="Nombre..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={
                      <span className="dark:text-gray-300">
                        {fieldState.error?.message}
                      </span>
                    }
                  />
                )}
              />
            </div>

            {/* Apellido Paterno */}
            <div className="space-y-2">
              <Label htmlFor="apellido_paterno" value="Apellido Paterno" />
              <Controller
                name="apellido_paterno"
                control={control}
                defaultValue=""
                rules={{
                  required: "Se requiere un apellido paterno",
                  maxLength: {
                    value: 32,
                    message:
                      "El apellido paterno debe tener menos de 32 caracteres",
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="apellido_paterno"
                    placeholder="Apellido..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Apellido Materno */}
            <div className="space-y-2">
              <Label htmlFor="apellido_materno" value="Apellido Materno" />
              <Controller
                name="apellido_materno"
                control={control}
                defaultValue=""
                rules={{
                  required: "Se requiere un apellido materno",
                  maxLength: {
                    value: 32,
                    message:
                      "El apellido materno debe tener menos de 32 caracteres",
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="apellido_materno"
                    placeholder="Apellido..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Correo */}
            <div className="space-y-2">
              <Label htmlFor="correo" value="Correo" />
              <Controller
                name="correo"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="correo"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatLowerCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Teléfono */}
            <div className="space-y-2">
              <Label htmlFor="telefono" value="Teléfono" />
              <Controller
                name="telefono"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="telefono"
                    type="tel"
                    placeholder="5512345678"
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Teléfono de recados */}
            <div className="space-y-2">
              <Label htmlFor="telefono_recados" value="Teléfono de recados" />
              <Controller
                name="telefono_recados"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <Textarea
                    id="telefono_recados"
                    placeholder="5512345678"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                    rows={5}
                  />
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Empresa */}
            <div className="space-y-2">
              <Label htmlFor="empresa_id" value="Empresa" />
              <Controller
                name="empresa_id"
                control={control}
                defaultValue=""
                rules={{ required: "Se requiere una empresa" }}
                render={({ field, fieldState }) => (
                  <Select
                    id="empresa_id"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  >
                    <option value="">Elige una Empresa</option>
                    {queryEmpresas.data?.map((empresa) => (
                      <option key={empresa.id} value={empresa.id}>
                        {empresa.nombre}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </div>

            {/* Empresa en la que labora */}
            <div className="space-y-2">
              <Label
                htmlFor="empresa_labora"
                value="Empresa en la que labora"
              />
              <Controller
                name="empresa_labora"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="empresa_labora"
                    placeholder="Empresa en la que labora..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* ¿Se Postula o Evalúa? */}
            <div className="space-y-2">
              <Label htmlFor="sepostula_evalua" value="¿Se Postula o Evalúa?" />
              <Controller
                name="sepostula_evalua"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <select
                    id="sepostula_evalua"
                    {...field}
                    className={`w-full rounded-lg border ${
                      fieldState.error
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    } ${
                      isDarkMode
                        ? "bg-gray-700 text-white"
                        : "bg-white text-black"
                    } p-2.5`}
                  >
                    <option value="" disabled>
                      Seleccione una opción
                    </option>
                    <option value="Se postula">Se postula</option>
                    <option value="Se evalúa">Se evalúa</option>
                  </select>
                )}
              />
            </div>

            {/* Motivo de Evaluación */}
            <div className="space-y-2">
              <Label htmlFor="motivo_evaluacion" value="Motivo de Evaluación" />
              <Controller
                name="motivo_evaluacion"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="motivo_evaluacion"
                    placeholder="Motivo de evaluación..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Tipo de Evaluación */}
            <div className="space-y-2">
              <Label htmlFor="tipo_evaluacion" value="Tipo de Evaluación" />
              <Controller
                name="tipo_evaluacion"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <select
                    id="tipo_evaluacion"
                    {...field}
                    className={`w-full rounded-lg border ${
                      fieldState.error
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    } ${
                      isDarkMode
                        ? "bg-gray-700 text-white"
                        : "bg-white text-black"
                    } p-2.5`}
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="Nuevo Ingreso">Nuevo Ingreso</option>
                    <option value="Permanencia">Permanencia</option>
                    <option value="Ascenso">Ascenso</option>
                    <option value="Otro">Otro</option>
                  </select>
                )}
              />
            </div>

            {/* Puesto */}
            <div className="space-y-2">
              <Label htmlFor="puesto" value="Puesto" />
              <Controller
                name="puesto"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="puesto"
                    placeholder="Puesto..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Fecha de ingreso */}
            <div className="space-y-2">
              <Label htmlFor="fecha_ingreso" value="Fecha de Ingreso" />
              <Controller
                name="fecha_ingreso"
                control={control}
                defaultValue={null}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="fecha_ingreso"
                    type="date"
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Antigüedad */}
            <div className="space-y-2">
              <Label htmlFor="antiguedad" value="Antigüedad" />
              <Controller
                name="antiguedad"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="antiguedad"
                    type="text"
                    placeholder="Años de antigüedad"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Fecha de Nacimiento */}
            <div className="space-y-2">
              <Label htmlFor="fecha_nacimiento" value="Fecha de Nacimiento" />
              <Controller
                name="fecha_nacimiento"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="fecha_nacimiento"
                    type="date"
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Género */}
            <div className="space-y-2">
              <Label htmlFor="genero" value="Género" />
              <Controller
                name="genero"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <Select
                    id="genero"
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  >
                    <option value="">Elige un género</option>
                    <option value="M">Masculino</option>
                    <option value="F">Femenino</option>
                    <option value="O">Otro</option>
                  </Select>
                )}
              />
            </div>

            {/* Lugar de Nacimiento */}
            <div className="space-y-2">
              <Label htmlFor="lugar_nacimiento" value="Lugar de Nacimiento" />
              <Controller
                name="lugar_nacimiento"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="lugar_nacimiento"
                    placeholder="Lugar de Nacimiento..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Nacionalidad */}
            <div className="space-y-2">
              <Label htmlFor="nacionalidad" value="Nacionalidad" />
              <Controller
                name="nacionalidad"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="nacionalidad"
                    placeholder="Nacionalidad..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* CURP */}
            <div className="space-y-2">
              <Label htmlFor="curp" value="CURP" />
              <Controller
                name="curp"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="curp"
                    placeholder="CURP..."
                    {...field}
                    maxLength={18}
                    onChange={(e) => {
                      field.onChange(formatUpperCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* RFC */}
            <div className="space-y-2">
              <Label htmlFor="rfc" value="RFC" />
              <Controller
                name="rfc"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="rfc"
                    placeholder="RFC..."
                    {...field}
                    maxLength={13}
                    onChange={(e) => {
                      field.onChange(formatUpperCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Escolaridad */}
            <div className="space-y-2">
              <Label htmlFor="escolaridad" value="Escolaridad" />
              <Controller
                name="escolaridad"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="escolaridad"
                    placeholder="Escolaridad..."
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Calle */}
            <div className="space-y-2">
              <Label htmlFor="calle" value="Calle" />
              <Controller
                name="calle"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="calle"
                    placeholder="Calle..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Número exterior */}
            <div className="space-y-2">
              <Label htmlFor="numero_exterior" value="Número Exterior" />
              <Controller
                name="numero_exterior"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="numero_exterior"
                    placeholder="Número exterior..."
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Número interior */}
            <div className="space-y-2">
              <Label htmlFor="numero_interior" value="Número Interior" />
              <Controller
                name="numero_interior"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="numero_interior"
                    placeholder="Número interior"
                    {...field}
                  />
                )}
              />
            </div>

            {/* Referencia */}
            <div className="space-y-2">
              <Label htmlFor="referencia" value="Referencia" />
              <Controller
                name="referencia"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="referencia"
                    placeholder="Referencia"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Colonia */}
            <div className="space-y-2">
              <Label htmlFor="colonia" value="Colonia" />
              <Controller
                name="colonia"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="colonia"
                    placeholder="Colonia..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Código postal */}
            <div className="space-y-2">
              <Label htmlFor="codigo_postal" value="Código Postal" />
              <Controller
                name="codigo_postal"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="codigo_postal"
                    placeholder="Código Postal..."
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Municipio */}
            <div className="space-y-2">
              <Label htmlFor="municipio" value="Municipio" />
              <Controller
                name="municipio"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="municipio"
                    placeholder="Municipio..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Estado */}
            <div className="space-y-2">
              <Label htmlFor="estado" value="Estado" />
              <Controller
                name="estado"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="estado"
                    placeholder="Estado..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* País */}
            <div className="space-y-2">
              <Label htmlFor="pais" value="País" />
              <Controller
                name="pais"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="pais"
                    placeholder="País..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Años viviendo en ese domicilio */}
            <div className="space-y-2">
              <Label
                htmlFor="anios_domicilio_viviendo"
                value="Años Viviendo en ese Domicilio"
              />
              <Controller
                name="anios_domicilio_viviendo"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <Textarea
                    id="anios_domicilio_viviendo"
                    placeholder="Años..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    rows={5}
                  />
                )}
              />
            </div>

            {/* Personas en la vivienda */}
            <div className="space-y-2">
              <Label
                htmlFor="personas_vivienda"
                value="Personas en la Vivienda"
              />
              <Controller
                name="personas_vivienda"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="personas_vivienda"
                    type="number"
                    placeholder="Personas en la vivienda..."
                    {...field}
                    onChange={(e) => handleNumberChange(e, field)}
                    min="0"
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Estado Civil */}
            <div className="space-y-2">
              <Label htmlFor="estado_civil" value="Estado Civil" />
              <Controller
                name="estado_civil"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <Select
                    id="estado_civil"
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  >
                    <option value="">Elige un estado civil</option>
                    <option value="soltero">Soltero</option>
                    <option value="casado">Casado</option>
                    <option value="concubinato">Concubinato</option>
                    <option value="divorciado">Divorciado</option>
                    <option value="viudo">Viudo</option>
                  </Select>
                )}
              />
            </div>

            {/* Con quien vive */}
            <div className="space-y-2">
              <Label htmlFor="vive_con" value="Con quien vive" />
              <Controller
                name="vive_con"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="vive_con"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    placeholder="Escribe con quien vive"
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* TIEMPO DE CASADO(A) Y/O CONCUBINATO */}
            <div className="space-y-2">
              <Label
                htmlFor="tiempo_casado"
                value="Tiempo de casado(a) y/o concubinato"
              />
              <Controller
                name="tiempo_casado"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="tiempo_casado"
                    type="number"
                    placeholder="Tiempo de casado(a)..."
                    {...field}
                    onChange={(e) => handleNumberChange(e, field)}
                    min="0"
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* A QUE SE DEDICA SU ESPOSA(O) Y/O CONCUBINO */}
            <div className="space-y-2">
              <Label
                htmlFor="conyuge_oficio"
                value="A que se dedica su esposo(a) y/o concubino"
              />
              <Controller
                name="conyuge_oficio"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="conyuge_oficio"
                    placeholder="A que se dedica su esposo(a)..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* NOMBRE DE ESPOSA(O) Y/O CONCUBINO */}
            <div className="space-y-2">
              <Label
                htmlFor="nombre_conyuge"
                value="Nombre de esposa(o) y/o concubino"
              />
              <Controller
                name="nombre_conyuge"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="nombre_conyugue"
                    placeholder="Nombre..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* CUANTOS HIJOS TIENE */}
            <div className="space-y-2">
              <Label htmlFor="cuantos_hijos" value="Cuantos hijos tiene" />
              <Controller
                name="cuantos_hijos"
                control={control}
                defaultValue=""
                rules={{
                  max: { value: 4, message: "Máximo 4 hijos permitidos" },
                  min: { value: 0, message: "Mínimo 0 hijos" },
                  validate: (value) => {
                    if (value < 0) {
                      return "No se permiten números negativos";
                    }
                    if (value > 4) {
                      return "Máximo 4 hijos permitidos";
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="cuantos_hijos"
                    type="number"
                    placeholder="Hijos..."
                    {...field}
                    min={0}
                    max={4}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const clampedValue = Math.min(Math.max(value, 0), 4);
                      field.onChange(clampedValue);
                      setNumeroHijos(clampedValue);
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {Array.from({ length: numeroHijos }, (_, index) => (
              <div key={index} className="space-y-2">
                {/* NOMBRE HIJO */}
                <Label
                  htmlFor={`nombreh${index + 1}`}
                  value={`Nombre del Hijo ${index + 1}`}
                />
                <Controller
                  name={`nombreh${index + 1}`}
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState }) => (
                    <TextInput
                      id={`nombreh${index + 1}`}
                      placeholder="Nombre..."
                      {...field}
                      onChange={(e) => {
                        field.onChange(formatTitleCase(e.target.value));
                      }}
                      color={fieldState.error ? "failure" : "gray"}
                      helperText={fieldState.error?.message}
                      className={
                        isDarkMode
                          ? "bg-gray-700 text-black"
                          : "bg-white text-black"
                      }
                    />
                  )}
                />

                {/* EDAD HIJO */}
                <Label
                  htmlFor={`edadh${index + 1}`}
                  value={`Edad del Hijo ${index + 1}`}
                />
                <Controller
                  name={`edadh${index + 1}`}
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState }) => (
                    <TextInput
                      id={`edadh${index + 1}`}
                      type="number"
                      placeholder="Edad..."
                      {...field}
                      color={fieldState.error ? "failure" : "gray"}
                      helperText={fieldState.error?.message}
                      className={
                        isDarkMode
                          ? "bg-gray-700 text-black"
                          : "bg-white text-black"
                      }
                    />
                  )}
                />
              </div>
            ))}
          </div>

          <div className="pt-4 flex space-x-4">
            <Button
              type="submit"
              className={`w-full ${
                isDarkMode
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-green-600 hover:bg-green-700"
              } text-white`}
            >
              Crear
            </Button>
            <Button
              type="button"
              onClick={() => {
                handleCancel();
                window.scrollTo(0, 0);
              }}
              className={`w-full ${
                isDarkMode
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-red-600 hover:bg-red-700"
              } text-white`}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
